import React, { useState } from "react";


import {
    Button,
    CropIcon,
    DeleteIcon,
    ImageAspectRatio,
    ImageCropper,
    OrderButtons,
    SectionCard,
    Spinner,
    UploadDropzone,
} from "shared/ui";
import {
    MENU_ITEM_MEDIA_ACCEPT,
    MENU_ITEM_MEDIA_HINT,
    UNSUPPORTED_MEDIA_ERROR,
    validateMenuItemMedia,
} from "shared/lib/mediaUpload";
import { replaceMenuItemRendition, uploadMenuItemMedia } from "services/uploads/rest";
import { MenuItemRendition } from "services/uploads/restModels";
import { MediaItemDto, MediaKind } from "services/menu/restModels";

import { useMenuItemEditorContext } from "features/MenuItemEditor/context";

import { Actions, Dot, List, Meta, MetaLabel, Preview, Row, VideoPreview } from "./styles";

const getImageLink = (media: MediaItemDto) => media.card || media.source;

const mediaLabel: Record<MediaKind, string> = {
    [MediaKind.image]: "Фотография",
    [MediaKind.gif]: "Гифка",
    [MediaKind.video]: "Видео",
};

export const PhotosTab = () => {
    const state = useMenuItemEditorContext();

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSubtypeUploading, setIsSubtypeUploading] = useState<boolean>(false);
    const [currentFile, setCurrentFile] = useState<File | undefined>();
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [rendition, setRendition] = useState<MenuItemRendition>("card");
    const [cropOpen, setCropOpen] = useState<boolean>(false);

    const showUploadError = () => state.notify.showError("Не удалось загрузить файл");

    const uploadMedia = async (files: File[]) => {
        // Пустой список — дропзона отсеяла файл по accept. Без своего сообщения перетаскивание
        // «неправильного» файла выглядит так, будто кабинет молча ничего не сделал.
        if (files.length === 0) {
            state.notify.showError(UNSUPPORTED_MEDIA_ERROR);
            return;
        }

        const file = files[0];
        const { kind, error } = await validateMenuItemMedia(file);

        if (kind === null || error !== null) {
            state.notify.showError(error);
            return;
        }

        setIsUploading(true);
        try {
            state.setMedia([...state.media, await uploadMenuItemMedia(file)]);
        } catch {
            showUploadError();
        } finally {
            setIsUploading(false);
        }
    };

    const getFile = async (url: string) => {
        const response = await fetch(url);
        const blob = await response.blob();
        setCurrentFile(new File([blob], "image.jpeg"));
    };

    const openCrop = async (index: number, next: MenuItemRendition) => {
        setRendition(next);
        setCurrentIndex(index);
        await getFile(state.media[index].source);
        setCropOpen(true);
    };

    const customizeImage = async (file: File) => {
        if (currentIndex == null) return;
        const current = state.media[currentIndex];
        if (!current?.source) return;

        setIsSubtypeUploading(true);
        try {
            const res = await replaceMenuItemRendition(current.sourceId, rendition, file);
            const updated = [...state.media];
            updated[currentIndex] = { ...current, [rendition]: res.url };
            state.setMedia(updated);
        } catch {
            showUploadError();
        } finally {
            setIsSubtypeUploading(false);
        }
    };

    return (
        <SectionCard
            title="Медиа позиции"
            description="Первый файл — главный, его видят в списке меню. В списке ничего не проигрывается: гость видит первый кадр. Гифка оживает при открытии карточки, видео — по кнопке. Превью и карточку можно обрезать только у фотографии."
        >
            <List>
                {state.media.map((media, index) =>
                    isSubtypeUploading && index === currentIndex ? (
                        <Row key={media.source || index}>
                            <Spinner color="secondary" />
                        </Row>
                    ) : (
                        <Row key={media.source || index}>
                            <OrderButtons
                                onUp={() => state.moveMedia(index, "up")}
                                onDown={() => state.moveMedia(index, "down")}
                                disableUp={index === 0}
                                disableDown={index === state.media.length - 1}
                            />
                            {media.kind === MediaKind.video ? (
                                <VideoPreview src={media.source} preload="metadata" muted />
                            ) : (
                                <Preview
                                    src={`${getImageLink(media)}?${Math.random().toString(36)}`}
                                    alt=""
                                />
                            )}
                            <Meta>
                                <MetaLabel $main={index === 0}>
                                    {index === 0 && <Dot />}
                                    {index === 0
                                        ? `${mediaLabel[media.kind]} — главная`
                                        : `${mediaLabel[media.kind]} — дополнительная`}
                                </MetaLabel>
                                <Actions>
                                    {media.kind === MediaKind.image && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => openCrop(index, "card")}
                                            >
                                                <CropIcon fontSize="small" /> Превью
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => openCrop(index, "detail")}
                                            >
                                                <CropIcon fontSize="small" /> Карточка
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="danger"
                                        onClick={() => state.removeMedia(index)}
                                    >
                                        <DeleteIcon fontSize="small" /> Удалить
                                    </Button>
                                </Actions>
                            </Meta>
                        </Row>
                    )
                )}
            </List>

            {isUploading ? (
                <Spinner color="secondary" />
            ) : (
                <UploadDropzone
                    onDrop={uploadMedia}
                    width={340}
                    height={108}
                    accept={MENU_ITEM_MEDIA_ACCEPT}
                    hint={MENU_ITEM_MEDIA_HINT}
                />
            )}

            <ImageCropper
                open={cropOpen}
                onClose={() => setCropOpen(false)}
                image={currentFile}
                onCrop={customizeImage}
                aspectRatio={ImageAspectRatio.rectangle_3_2}
            />
        </SectionCard>
    );
};
